import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'shopcentral_wishlist';

function readIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function writeIds(ids: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  window.dispatchEvent(new CustomEvent('wishlist-change'));
}

export function useWishlist() {
  const [ids, setIds] = useState<string[]>(() => readIds());

  useEffect(() => {
    const sync = () => setIds(readIds());
    window.addEventListener('wishlist-change', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('wishlist-change', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const isWishlisted = useCallback((id: string) => ids.includes(id), [ids]);

  const toggle = useCallback((id: string) => {
    const current = readIds();
    const next = current.includes(id) ? current.filter(i => i !== id) : [...current, id];
    writeIds(next);
    setIds(next);
  }, []);

  const remove = useCallback((id: string) => {
    const next = readIds().filter(i => i !== id);
    writeIds(next);
    setIds(next);
  }, []);

  return { ids, count: ids.length, isWishlisted, toggle, remove };
}
